import React from 'react';
import { Box, Button, Heading, PageContent, Spinner, Stack, Text } from 'bumbag';
import { Form, Formik } from 'formik';
import Script from 'react-load-script';
import { useDeferredLoads, useLoads } from 'react-loads-next';
import { useHistory } from 'react-router-dom';

import { WithErrorMessage } from '@medipass/react-core';
import { PaymentCardInputs } from '@medipass/react-core/redux-form';
import env from '@medipass/utils/get-env';
import sdk from '@medipass/web-sdk';

import LayoutPage from 'legacy/components/checkout/layout/Page';
import WithAuthentication from 'legacy/components/WithAuthentication';
import postMessage from 'legacy/utils/post-message';
import { getKeyFromQueryParams } from 'legacy/utils/router';

import { EXPIRED_PAYMENTMETHODS_URL } from '../../constants';
import Footer from '../../legacy/components/checkout/Footer/Footer';
import SecurityBox from './components/SecurityBox';

function UpdatePaymentMethodPage() {
  /////////////////////////////////////////////////////////////////////////
  const patientId = getKeyFromQueryParams('patientId');
  const businessId = getKeyFromQueryParams('businessId');
  const businessName = getKeyFromQueryParams('displayName');
  const callbackOrigin = getKeyFromQueryParams('callbackOrigin');
  const history = useHistory();

  /////////////////////////////////////////////////////////////////////////
  const onCancelUpdatePaymentDetails = () => {
    if (callbackOrigin) {
      postMessage({
        data: {
          event: 'cancel',
        },
        origin: callbackOrigin,
      });
    }

    history.push('/paymentmethods/canceled');
  };

  // @ts-expect-error TS(7006): Parameter 'error' implicitly has an 'any' type.
  const onFailureUpdatePaymentDetails = error => {
    if (callbackOrigin) {
      postMessage({
        data: {
          event: 'failure',
        },
        origin: callbackOrigin,
      });
    }
  };

  const onSuccessUpdatePaymentDetails = () => {
    if (callbackOrigin) {
      postMessage({
        data: {
          event: 'success',
        },
        origin: callbackOrigin,
      });
    }

    history.push('/paymentmethods/success', {
      businessName,
    });
  };

  /////////////////////////////////////////////////////////////////////////
  const getCurrentPaymentMethod = async () => {
    const paymentMethod = await sdk.payments.getBusinessPatientPaymentMethods(businessId, patientId, {
      requestConfig: {
        tokenType: 'session',
      },
    });
    return paymentMethod;
  };

  const paymentMethodLoader = useLoads('paymentDetails', getCurrentPaymentMethod, {
    variables: [businessId],
  });
  const paymentMethodId = paymentMethodLoader?.response?.items?.[0]?._id;

  /////////////////////////////////////////////////////////////////////////
  // @ts-expect-error TS(7006): Parameter 'paymentDetails' implicitly has an 'any'... Remove this comment to see the full error message
  const handleUpdatePaymentDetails = async paymentDetails => {
    const nonceResponse = await sdk.payments.getNonce({
      businessId,
      patientId,
      cardNumber: paymentDetails.cardNumber,
      expiry: paymentDetails.expiry,
      cvc: paymentDetails.cvc,
    });

    try {
      if (paymentMethodId) {
        await sdk.payments.deleteBusinessPatientPaymentMethod(businessId, patientId, paymentMethodId, {
          requestConfig: {
            tokenType: 'session',
          },
        });
      }

      await sdk.payments.createBusinessPatientPaymentMethod(
        businessId,
        patientId,
        (nonceResponse as any).nonce,
        (nonceResponse as any).source,
        {
          requestConfig: {
            tokenType: 'session',
          },
        },
      );
      onSuccessUpdatePaymentDetails();
    } catch (error) {
      onFailureUpdatePaymentDetails(error);
      // Throwing a blank error so as to not potentially tip-off fraudsters who might be using
      // dodgy card details.
      throw new Error();
    }
  };

  const handleUpdatePaymentDetailsLoader = useDeferredLoads(handleUpdatePaymentDetails);
  ////////////////////////////////////////////////////////////////////////////////////////////
  return (
    <React.Fragment>
      <Script url={env.HPF_SCRIPT_URL} />
      <WithAuthentication
        expiredTokenRedirectUrl={EXPIRED_PAYMENTMETHODS_URL}
        isSelfCheckoutSession
        requiresValidAccountToken={false}
        requiresValidSessionToken
      >
        <LayoutPage title="Update card details">
          <Formik
            initialValues={{
              cardNumber: '',
              expiry: '',
              cvc: '',
            }}
            onSubmit={data => handleUpdatePaymentDetailsLoader.load(data)}
          >
            <Form>
              <PageContent alignX="center" breakpoint="checkout">
                <Box>
                  <Text fontSize="1.5rem">Payment details request from</Text>
                  <Heading use="h4" alignX="center">
                    {businessName}
                  </Heading>
                  <Text alignX="right" color="text" fontSize="0.8rem">
                    {new Date().toLocaleDateString()}{' '}
                    {new Date().toLocaleTimeString('en-US', {
                      hour12: true,
                      hour: 'numeric',
                      minute: 'numeric',
                    })}
                  </Text>
                </Box>
              </PageContent>
              <PageContent
                alignX="center"
                breakpoint="checkout"
                wrapperProps={{
                  backgroundColor: 'white600',
                }}
              >
                <Box>
                  <SecurityBox />
                </Box>
                <PaymentCardInputs formType="formik" isRequired variant="wrapped" size="large" />
              </PageContent>
              <PageContent
                alignX="center"
                breakpoint="checkout"
                wrapperProps={{
                  border: 'none',
                }}
              >
                {handleUpdatePaymentDetailsLoader?.isRejected && (
                  <WithErrorMessage
                    marginBottom="major-2"
                    isActive
                    // @ts-expect-error TS(2322): Type 'unknown' is not assignable to type 'Record<s... Remove this comment to see the full error message
                    error={handleUpdatePaymentDetailsLoader?.error}
                    description="Unable to save payment details."
                  />
                )}
                <Stack spacing="minor-1" width="100%">
                  <Button
                    palette="secondary"
                    width="100%"
                    size="large"
                    type="submit"
                    isLoading={handleUpdatePaymentDetailsLoader.isPending}
                  >
                    {handleUpdatePaymentDetailsLoader.isPending ? (
                      <Spinner size="medium" color="white" />
                    ) : (
                      'Save payment details'
                    )}
                  </Button>
                  <Button
                    palette="#616161"
                    _hover={{ color: 'white' }}
                    variant="outlined"
                    width="100%"
                    size="large"
                    onClick={onCancelUpdatePaymentDetails}
                  >
                    Cancel
                  </Button>
                </Stack>
                <Stack marginTop="major-2" spacing="minor-3">
                  <Text.Block fontSize="0.8rem" width="100%">
                    Tyro Health collects your email, mobile, personal details, health fund and payment information to
                    process claims and payments. See our privacy policy for details on how we protect your information.
                  </Text.Block>
                </Stack>

                <Footer />
              </PageContent>
            </Form>
          </Formik>
        </LayoutPage>
      </WithAuthentication>
    </React.Fragment>
  );
}

export default UpdatePaymentMethodPage;
